import type { MouseEvent } from 'react';
import styled from 'styled-components';
import type { HygienePageNavigationLinks as NavigationLinksSchema } from '@amplience/content-types/typings/c-hygiene-page-navigation-links';
import { LeftArrow } from '@storefront/lib-global/icons';
import { ImagePartial } from '@storefront/lib-global/partials/image';
import { LinkPartial } from '@storefront/lib-global/partials/link';
import { TypographyStyles, media, spacing } from '@storefront/lib-global/stylings';
import { colours } from '@storefront/lib-global/stylings/colours';
import { sendSelectContentEvent } from '@storefront/lib-global/utils/gtm/events/select_content/sendSelectContentEvent';
import { getLocalizedValue } from '@storefront/lib-global/utils/transformers';

export type NavigationLinksProps = NavigationLinksSchema;

const S = {
  Container: styled.div<{ $isSideNavigationLink?: boolean }>`
    @media ${media.greaterThan('lg')} {
      ${({ $isSideNavigationLink }) => ($isSideNavigationLink ? 'width: 330px;' : '')}
      max-width: 777px;
    }
  `,
  SimilarLinksHeading: styled.h2`
    ${TypographyStyles.Headings.H3}
    margin-bottom: 28px;
    margin-top: 0;

    @media ${media.greaterThan('lg')} {
      margin-bottom: ${spacing.M};
    }
  `,
  SimilarLinksList: styled.ul<{ $isSideNavigationLink?: boolean }>`
    all: unset;
    list-style-type: none;
    padding: 0;

    @media ${media.greaterThan('lg')} {
      column-gap: 30px;
      display: flex;
      flex-wrap: wrap;
      ${({ $isSideNavigationLink }) => ($isSideNavigationLink ? 'row-gap:30px;' : '')}
    }
  `,
  SimilarLinksListItem: styled.li<{ $isSideNavigationLink?: boolean }>`
    all: unset;
    display: flex;
    height: 82px;
    width: 100%;
    @media ${media.greaterThan('lg')} {
      width: ${({ $isSideNavigationLink }) => ($isSideNavigationLink ? '100%' : 'calc(50% - 15px)')};
    }
  `,
  SimilarLink: styled(LinkPartial)`
    align-items: center;
    display: flex;
    text-decoration: none;
    width: 100%;

    &:hover,
    &:focus {
      svg {
        transform: translateX(4px);
      }
      text-decoration: underline;
    }
  `,
  LinkIconContainer: styled.div`
    align-items: center;
    aspect-ratio: 1;
    background-color: ${colours.LIGHT_BLUE};
    border-radius: 50%;
    display: flex;
    height: ${spacing.L};
    justify-content: center;
    margin-right: ${spacing.XS};
    position: relative;
    width: ${spacing.L};
  `,
  LinkText: styled.span`
    color: ${colours.BLACK};
    ${TypographyStyles.Body.Small.SemiBold}
    max-width: max-content;
    word-break: break-word;
  `,
  LinkArrow: styled.div`
    height: 20px;
    margin-left: auto;
    width: 20px;
  `,
};

export function HygienePageNavigationLinks({
  heading,
  hygienePageNavigationLinks,
  isSideNavigationLink,
  displayOption,
}: NavigationLinksProps) {
  const handleClick = (text: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    const element = event.currentTarget as HTMLAnchorElement;

    sendSelectContentEvent({
      component_type: 'Tab Navigation Links',
      component_header: getLocalizedValue(heading ?? '').trim(),
      link_url: element.href,
      link_text: text,
    });
  };

  return (
    <S.Container $isSideNavigationLink={isSideNavigationLink}>
      {heading && <S.SimilarLinksHeading>{getLocalizedValue(heading)}</S.SimilarLinksHeading>}
      <S.SimilarLinksList>
        {hygienePageNavigationLinks?.map((link, index) => (
          <S.SimilarLinksListItem
            $isSideNavigationLink={isSideNavigationLink}
            key={`${link.link?.internalLink}-${index}`}
          >
            <S.SimilarLink {...link.link} onClick={handleClick(getLocalizedValue(link.bodyText))}>
              {displayOption !== 'Bodytext Only' && (
                <S.LinkIconContainer>
                  <ImagePartial
                    height={15}
                    width={15}
                    layout="intrinsic"
                    src={link?.image?.image?.name || ''}
                    assetType={link?.image?.assetType}
                    defaultHost={link?.image?.image?.defaultHost}
                    useOriginalSize={link?.image?.useOriginalSize}
                    aria-hidden
                  />
                </S.LinkIconContainer>
              )}
              {displayOption !== 'Icon/Image Only' && <S.LinkText>{getLocalizedValue(link.bodyText)}</S.LinkText>}
              <S.LinkArrow>
                <LeftArrow aria-hidden />
              </S.LinkArrow>
            </S.SimilarLink>
          </S.SimilarLinksListItem>
        ))}
      </S.SimilarLinksList>
    </S.Container>
  );
}
